import React, { useState } from 'react';
import { IconButton, Modal, withStyles } from '@material-ui/core';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { useTranslation } from 'react-i18next';
import PF2MDataTable from '~/components/PF2MDataTable';
import Photo from '@material-ui/icons/Photo';
import {
  newActivityList,
  setActivityValues,
  deleteActivityItem,
  restoreOriginalActivity,
  updateActivityList,
  uploadActivityPicture,
  searchActivityList,
} from '~/store/dailyparts/actions';
import moment from 'moment';
import styles from '../styles';
import PictureModal from './PictureModal';
import usePlatformConfiguration, {
  usePlatformConfigurationUserAccessLevel,
} from '~/services/platformConfiguration';
import {
  subtractDaysFromNow,
  formatTimestamp,
  convertTimeStampToMoment,
  newMomentDate,
  parseDate,
} from '~/utils/moment';
import EditHistoryModal from '~/components/EditHistoryModal';
import EditHistoryButton from '~/components/EditHistoryButton';
import { usePagination } from '../hooks';

const formatActivityList = row => ({
  ...row,
  timestamp_op: row.timestamp_op * 1000,
  hour: Number(row.hour).toFixed(2),
});

const unFormatActivityList = row => ({
  ...row,
  timestamp_op: row.timestamp_op / 1000,
  hour: Number(row.hour),
  code_id: row.code_id === '0' ? null : row.code_id,
  op_group: row.op_group === '0' ? null : row.op_group,
  op: row.op === '0' ? null : row.op,
  area_id: row.area_id === '0' ? null : row.area_id,
  subarea_id: row.subarea_id === '0' ? null : row.subarea_id,
});

const ActivityList = () => {
  const { t: translate } = useTranslation();

  const rawActivityList = useSelector(state => state.dailyParts.activityList);
  const filters = useSelector(state => state.dailyParts.filters);
  const userAccessLevel = useSelector(state => state.auth.user.level);
  const equipmentGroups = useSelector(state => state.manager.equipmentsGroups
    .filter(eg => eg.id_equipament === filters.equipmentType), shallowEqual);
  const isActivityListDirty = useSelector(st => st.dailyParts.isActivityListDirty);
  const codeTypes = useSelector(state => state.manager.codeTypes);
  const codeGroups = useSelector(state => state.manager.codeGroups);
  const codes = useSelector(state => state.manager.codes);
  const equipments = useSelector(state => state.manager.equipments
    .filter(equip => equip.id_equip === filters.equipmentType), shallowEqual);
  const operatorsGroups = useSelector(state => state.manager.operatorsGroups);
  const operators = useSelector(state => state.manager.operators);
  const elements = useSelector(state => state.manager.elements);
  const subElements = useSelector(state => state.manager.subElements);
  const exceptionTypes = useSelector(state => state.manager.exceptionTypes);
  const closures = useSelector(state => state.manager.closures);
  const ogList = useSelector(state => state.dailyParts.originalActivityList);
  const activityPagination = useSelector(
    state => state.dailyParts.pagination.activityPagination,
  );

  const [pictureModalData, setPictureModalData] = useState(null);
  const [openEditHistoryModal, setOpenEditHistoryModal] = useState(false);
  const [selectedEditHistoryData, setSelectedEditHistoryData] = useState(null);
  const dispatch = useDispatch();

  const activityList = rawActivityList.map(formatActivityList);

  const { value: userLimitEditConfig } = usePlatformConfigurationUserAccessLevel(userAccessLevel);
  const { value: enableClosure } = usePlatformConfiguration('enable_closure');

  const validateDates = type => (row) => {
    let userLimitEdit = subtractDaysFromNow(userLimitEditConfig);
    const isEnableClosureOn = enableClosure && closures?.length > 0;

    if (isEnableClosureOn) {
      const dateNow = newMomentDate();
      const lastClosureDate = parseDate(closures[0].timestampClose, true);
      const currentDaysFromLastClosure = dateNow.diff(lastClosureDate, 'days');

      const lockEditConfig = subtractDaysFromNow(userLimitEditConfig);
      const lockEditLastClosure = subtractDaysFromNow(currentDaysFromLastClosure);

      userLimitEdit = currentDaysFromLastClosure > userLimitEditConfig
        ? lockEditConfig : lockEditLastClosure;
    }

    const momentDate = convertTimeStampToMoment(formatTimestamp(row[type]));
    if (type === 'timestamp_op' && momentDate.isBefore(userLimitEdit)) {
      return { isValid: false, helperText: translate('validation:InvalidPeriodDate') };
    }

    return true;
  };

  const columns = [
    {
      editable: 'never',
      title: translate('common:EditHistory'),
      field: 'edit_history',
      render: row => (
        <EditHistoryButton
          exceptionType={row.exception_type}
          onClick={() => {
            setOpenEditHistoryModal(true);
            setSelectedEditHistoryData(row);
          }}
        />
      ),
    },
    {
      editable: 'always',
      title: translate('common:InitialDate'),
      field: 'timestamp_op',
      customType: 'dateTimeByTimestamp',
      validate: validateDates('timestamp_op'),
    },
    {
      editable: 'always',
      title: translate('common:EquipmentGroup'),
      field: 'eq_group',
      lookup: equipmentGroups,
      validate: row => Boolean(row.eq_group),
      tooltip: true,
    },
    {
      editable: 'always',
      title: translate('common:Equipment'),
      customType: 'filterLookup',
      field: 'eq_id',
      lookup: equipments,
      lookupFilter: (opt, row) => (
        String(opt.id_group) === String(row.eq_group)
          || String(row.eq_group) === '0'
      ),
      validate: row => Boolean(row.eq_id) && String(row.eq_id) !== '0',
      tooltip: true,
    },
    {
      editable: 'always',
      title: translate('common:Hourmeter'),
      field: 'hour',
      type: 'numeric',
      tooltip: true,
    },
    {
      title: translate('common:Picture'),
      editable: false, // avoid problems with default editable
      field: 'picture_name',
      render: row => (
        <IconButton
          style={{ outline: 'none', color: row.picture_name ? 'rgb(242 170 0)' : 'red' }}
          onClick={() => { setPictureModalData(row); }}
        >
          <Photo />
        </IconButton>
      ),
      tooltip: true,
    },
    {
      editable: 'always',
      title: translate('common:DistanceKm'),
      field: 'kilo',
      type: 'numeric',
      tooltip: true,
    },
    {
      title: translate('common:Condition'),
      field: 'is_start',
      lookup: [
        { id: 0, name: translate('common:End') },
        { id: 1, name: translate('common:Start') },
      ],
      tooltip: true,
    },
    {
      editable: 'always',
      title: translate('common:CodeType'),
      field: 'code_type_id',
      lookup: codeTypes.map(cType => ({
        id: cType.id,
        name: translate(`common:${cType.name}`),
      })),
      tooltip: true,
    },
    {
      title: translate('common:CodeGroup'),
      field: 'code_group_id',
      customType: 'filterLookup',
      lookup: codeGroups,
      lookupFilter: (codeGroupOption, row) => (
        String(codeGroupOption.code_type) === String(row.code_type_id)
        && String(row.eq_type) === String(codeGroupOption.id_equip)
      ),
      tooltip: true,
    },
    {
      title: translate('common:Code'),
      field: 'code_id',
      customType: 'filterLookup',
      lookup: codes,
      render: rowData => codes.find(
        e => e.id_group === rowData.code_group_id
        && e.id_pk === rowData.code_id,
      )?.name || null,
      lookupFilter: (codeOption, row) => {
        // No code Group Selected
        if (String(row.code_group_id) === '0') {
          return (
            String(row.code_type_id) === String(codeOption.code_type)
            && String(row.eq_type) === String(codeOption.id_equip)
          );
        }
        return (
          String(codeOption.id_group) === String(row.code_group_id)
          && String(row.eq_type) === String(codeOption.id_equip)
        );
      },
      tooltip: true,
      lookupKey: 'id_pk',
    },
    {
      editable: 'always',
      title: translate('common:OperatorGroup'),
      field: 'op_group',
      lookup: operatorsGroups,
      tooltip: true,
    },
    {
      editable: 'always',
      title: translate('common:Operator'),
      field: 'op',
      customType: 'filterLookup',
      lookupKey: 'id_operator',
      lookup: operators,
      lookupFilter: (opt, row) => String(opt.id_group) === String(row.op_group)
        || String(row.op_group) === '0',
      tooltip: true,
    },
    {
      editable: 'never',
      title: translate('common:EntryType'),
      field: 'exception_type',
      lookup: exceptionTypes,
      tooltip: true,
    },
    {
      editable: 'always',
      title: translate('common:Origin'),
      customType: 'filterLookup',
      field: 'area_id',
      lookup: elements,
      tooltip: true,
    },
    {
      editable: 'always',
      title: translate('common:OriginPoint'),
      customType: 'filterLookup',
      field: 'subarea_id',
      lookup: subElements,
      lookupFilter: (opt, row) => String(opt.id_element) === String(row.area_id),
      tooltip: true,
    },
    { title: translate('common:Latitude'), field: 'latitude', type: 'numeric' },
    { title: translate('common:Longitude'), field: 'longitude', type: 'numeric' },
    { title: translate('common:Altitude'), field: 'altitude', type: 'numeric' },
  ];

  const customEditHistoryColumns = [
    {
      title: translate('common:InitialDate'),
      field: 'timestamp_op',
      customType: 'dateTimeByTimestamp',
      render: row => (
        moment.unix(row.timestamp_op).format(translate('date:FullDateTimeFormat'))
      ),
    },
    {
      title: translate('common:CodeGroup'),
      field: 'code_group_id',
      lookup: codeGroups,
    },
    {
      title: translate('common:CodeType'),
      field: 'code_type_id',
      lookup: codeTypes.map(ct => ({ ...ct, name: translate(`common:${ct.name}`) })),
    },
  ];

  const uploadImage = (act) => {
    const activity = unFormatActivityList(act);
    dispatch(
      uploadActivityPicture(
        activity,
      ),
    );
  };

  const filterToFormFieldMap = {
    equipmentType: 'eq_type',
    equipmentGroup: 'eq_group',
    equipment: 'eq_id',
    codeType: 'code_type_id',
    codeGroup: 'code_group_id',
    code: 'code_id',
    operatorGroup: 'op_group',
    operator: 'op',
    startOrigin: 'area_id',
    startSubOrigin: 'subarea_id',
  };

  const filterToFormMultiFieldMap = {
    equipmentGroup: 'eq_group',
    equipment: 'eq_id',
  };

  const mappedFilterData = Object.entries(filterToFormFieldMap).reduce(
    (acc, [filterKey, formField]) => {
      if (filters[filterKey] !== undefined && filters[filterKey] !== 0) {
        if (filterKey === 'codeType' || filterKey === 'codeGroup' || filterKey === 'code') {
          acc[formField] = Number(filters[filterKey]);
        } else {
          acc[formField] = filters[filterKey];
        }
      }
      return acc;
    },
    {},
  );

  const mappedFilterMultiData = Object.entries(filterToFormMultiFieldMap).reduce(
    (acc, [filterKey, formField]) => {
      const filterValues = filters[filterKey];

      // adiciona para initialFormData somente
      // se tiver apenas um item selecionado no multiselect
      if (filterValues && filterValues.length === 1) {
        const [firstValue] = filterValues;
        acc[formField] = firstValue;
      } else {
      // se tiver mais de um ou nenhum, seta como '0'
        acc[formField] = '0';
      }
      return acc;
    },
    {},
  );

  const { applyDefaultSort, ...paginationOptions } = usePagination({
    filters,
    dispatch,
    listType: 'activity',
    pagination: activityPagination,
    searchAction: searchActivityList,
  });

  const { page = 0, total = 0, loadingMore = false } = activityPagination || { };

  return (
    <>
      <PictureModal
        modalData={pictureModalData}
        closeModal={() => setPictureModalData(null)}
        uploadImage={uploadImage}
      />
      <PF2MDataTable
        title={translate('common:Cycles')}
        initialFormData={
          unFormatActivityList({
            ...columns.reduce((acc, e) => ({ ...acc, [e.field]: null }), {}),
            eq_type: filters.equipmentType,
            picture_name: '',
            timestamp_op: new Date().getTime(),
            is_start: 0,
            hour: 0,
            kilo: 0,
            latitude: 0,
            longitude: 0,
            altitude: 0,
            ...mappedFilterData,
            ...mappedFilterMultiData,
          })
        }
        columns={applyDefaultSort(columns)}
        data={activityList.length > 0 ? activityList : []}
        page={page}
        totalCount={total}
        isLoading={loadingMore}
        options={{
          pageSize: 10,
          pageSizeOptions: [10, 20, 50, 100],
          showTitle: true,
          search: true,
          selection: true,
          hideCommit: !isActivityListDirty,
          hideReload: !isActivityListDirty,
          cacheKey: 'daily_parts_activity',
          showFirstLastPageButtons: false,
          ...paginationOptions,
        }}
        onAdd={newData => dispatch(newActivityList(newData))}
        onDiscard={() => dispatch(restoreOriginalActivity())}
        onApply={() => {
          const data = activityList.map(unFormatActivityList);
          const actionData = {
            data,
            parameters: filters,
            saveLog: true,
            ogData: ogList.filter(ogl => data.some(({ id }) => id === ogl.id)),
          };

          dispatch(updateActivityList(actionData));
        }}
        onChange={(newData) => {
          newData.forEach((row) => {
            dispatch(setActivityValues(unFormatActivityList(row)));
          });
        }}
        onDelete={(newData) => {
          newData.forEach(({ tableData, ...activityItem }) => {
            dispatch(deleteActivityItem(unFormatActivityList(activityItem)));
          });
        }}
      />
      <Modal
        open={openEditHistoryModal}
        onClose={() => setOpenEditHistoryModal(false)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <EditHistoryModal
          listName="activity"
          referenceColumns={columns}
          customEditHistoryColumns={customEditHistoryColumns}
          selectedEditHistoryData={selectedEditHistoryData}
          handleClose={() => setOpenEditHistoryModal(false)}
        />
      </Modal>
    </>
  );
};

ActivityList.propTypes = {};
ActivityList.defaultProps = {};

export default withStyles(styles)(ActivityList);
